// ─────────────────────────────────────────────────────────────────────────────
// The imperative half: `openFileViewer()` from anywhere, including code that
// is not React, the same way `openConfirmModal()` works. `<FileViewerProvider>`
// subscribes and renders the one viewer.
// ─────────────────────────────────────────────────────────────────────────────

import type { FileViewerOptions, FileViewerSource } from './types.js';

export interface FileViewerState {
  opened: boolean;
  options: FileViewerOptions;
  /** Bumps on every open, so the same source opened twice still resets. */
  session: number;
}

type Listener = () => void;

let state: FileViewerState = { opened: false, options: {}, session: 0 };
const listeners = new Set<Listener>();

function emit(next: FileViewerState): void {
  state = next;
  for (const l of listeners) l();
}

export const fileViewerStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  getState(): FileViewerState {
    return state;
  }
};

export type OpenFileViewerInput = FileViewerSource | FileViewerOptions;

function isSource(input: OpenFileViewerInput): input is FileViewerSource {
  return 'kind' in input && (input.kind === 'local' || input.kind === 'remote' || input.kind === 'blob');
}

/**
 * Open the viewer. Pass a source, or options carrying a source and a
 * collection to walk with the arrow keys. With nothing at all the chooser opens.
 */
export function openFileViewer(input: OpenFileViewerInput = {}): void {
  const options: FileViewerOptions = isSource(input) ? { source: input } : input;
  emit({ opened: true, options, session: state.session + 1 });
}

export function closeFileViewer(): void {
  if (!state.opened) return;
  emit({ ...state, opened: false });
}
